import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";

// ============================================================
// CONFIG
// ============================================================
const DB_NAME = "stl-thumbnail-cache-v3";
const STORE_NAME = "thumbnails";
const DB_VERSION = 1;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const AUTO_GENERATE_LIMIT_BYTES = 15 * 1024 * 1024; // 15MB
const THUMB_W = 280;
const THUMB_H = 260;

// Throttle: only N thumbnail generations at once
const MAX_CONCURRENT = 1;
// Delay between generations to avoid blocking main thread
const INTER_TASK_DELAY_MS = 8;

// ============================================================
// CACHE (memory + IndexedDB)
// ============================================================
const memoryCache = new Map();
let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);

      const req = indexedDB.open(DB_NAME, DB_VERSION);

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

  return dbPromise;
};

const readCache = async (key) => {
  if (memoryCache.has(key)) return memoryCache.get(key);

  const db = await openDb();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const req = db
        .transaction(STORE_NAME, "readonly")
        .objectStore(STORE_NAME)
        .get(key);

      req.onsuccess = () => {
        const rec = req.result;
        if (rec && rec.dataUrl && Date.now() - rec.savedAt < MAX_AGE_MS) {
          memoryCache.set(key, rec.dataUrl);
          resolve(rec.dataUrl);
        } else {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

const writeCache = async (key, dataUrl) => {
  memoryCache.set(key, dataUrl);

  const db = await openDb();
  if (!db) return;

  try {
    db.transaction(STORE_NAME, "readwrite")
      .objectStore(STORE_NAME)
      .put({ dataUrl, savedAt: Date.now() }, key);
  } catch {
    /* ignore */
  }
};

// ============================================================
// SHARED RENDERER (reused across thumbnail generations)
// ============================================================
let sharedRenderer = null;

const getRenderer = () => {
  if (sharedRenderer) return sharedRenderer;

  const renderer = new THREE.WebGLRenderer({
    antialias: false,          // faster
    preserveDrawingBuffer: true, // needed for toDataURL; keep true
    powerPreference: "low-power",
    alpha: false,
    stencil: false,
    depth: true,
  });
  renderer.setPixelRatio(1);
  renderer.setSize(THUMB_W, THUMB_H, false);
  renderer.setClearColor(0x1a1a1a, 1);

  // Ensure the canvas is not attached to the DOM but still renders
  renderer.domElement.style.position = "absolute";
  renderer.domElement.style.left = "-9999px";
  renderer.domElement.style.top = "-9999px";

  sharedRenderer = renderer;
  return renderer;
};

// ============================================================
// QUEUE (serialize heavy work to avoid jank)
// ============================================================
let activeCount = 0;
const pendingQueue = [];

const runNext = () => {
  if (activeCount >= MAX_CONCURRENT) return;
  const next = pendingQueue.shift();
  if (!next) return;

  activeCount++;
  next.task()
    .then(next.resolve, next.reject)
    .finally(() => {
      activeCount--;
      // Small delay so the browser can breathe / paint
      setTimeout(runNext, INTER_TASK_DELAY_MS);
    });
};

const enqueue = (task) =>
  new Promise((resolve, reject) => {
    pendingQueue.push({ task, resolve, reject });
    runNext();
  });

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// ============================================================
// HELPERS
// ============================================================
const isPlyUrl = (url) => {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return clean.endsWith(".ply");
};

/**
 * Remove non-essential attributes to save GPU memory.
 */
const prepareGeometry = (geometry) => {
  if (!geometry) return geometry;

  if (!geometry.attributes.normal) {
    geometry.computeVertexNormals();
  }

  const keep = ["position", "normal", "color"];
  Object.keys(geometry.attributes).forEach((name) => {
    if (!keep.includes(name)) {
      geometry.deleteAttribute(name);
    }
  });

  return geometry;
};

/**
 * Parse geometry with multiple fallbacks.
 */
const parseGeometry = (buffer, url) => {
  const ply = isPlyUrl(url);

  if (ply) {
    const loader = new PLYLoader();

    try {
      const g = loader.parse(buffer);
      if (g && g.attributes && g.attributes.position) {
        return prepareGeometry(g);
      }
    } catch (err) {
      console.warn("[PLY] ArrayBuffer parse failed:", err?.message);
    }

    try {
      const text = new TextDecoder("utf-8").decode(
        buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer
      );
      const g = loader.parse(text);
      if (g && g.attributes && g.attributes.position) {
        return prepareGeometry(g);
      }
    } catch (err) {
      console.error("[PLY] String parse failed:", err?.message);
    }

    throw new Error("PLY parse failed");
  }

  const g = new STLLoader().parse(buffer);
  return prepareGeometry(g);
};

// ============================================================
// THUMBNAIL GENERATION
// ============================================================
const generateThumbnail = async (fileUrl, signal) => {
  // Single fetch — no HEAD preflight (saves a round trip)
  const res = await fetch(fileUrl, {
    credentials: "include",
    signal,
    cache: "force-cache", // let browser cache help
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const buffer = await res.arrayBuffer();
  if (signal.aborted) throw new Error("aborted");

  const geometry = parseGeometry(buffer, fileUrl);

  geometry.center();
  geometry.computeBoundingSphere();
  const radius = geometry.boundingSphere?.radius || 1;

  const material = new THREE.MeshStandardMaterial({
    color: 0xdddddd,
    metalness: 0.1,
    roughness: 0.6,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geometry, material);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#1a1a1a");
  scene.add(mesh);

  const fov = 45;
  const distance = (radius / Math.sin((fov * Math.PI) / 360)) * 1.15;

  const camera = new THREE.PerspectiveCamera(
    fov,
    THUMB_W / THUMB_H,
    Math.max(distance / 100, 0.001),
    Math.max(distance * 10, 1)
  );
  camera.position.set(0, 0, distance);
  camera.lookAt(0, 0, 0);

  scene.add(new THREE.AmbientLight(0xffffff, 0.9));

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.6);
  keyLight.position.set(1, 1.4, 1.2);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
  fillLight.position.set(-1.2, -0.5, 1);
  scene.add(fillLight);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x333333, 0.9));

  try {
    const renderer = getRenderer();
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL("image/jpeg", 0.8);
    return dataUrl;
  } finally {
    geometry.dispose();
    material.dispose();
  }
};

// ============================================================
// COMPONENT
// ============================================================
const LazySTLThumbnail = ({
  fileUrl,
  height = "100%",
  onViewClick,
  cacheKey,
}) => {
  const key = cacheKey || fileUrl;

  const wrapRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [src, setSrc] = useState(memoryCache.get(key) || null);
  const [status, setStatus] = useState(
    memoryCache.get(key) ? "ready" : "idle"
  );

  // IntersectionObserver: only start work when near viewport
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "80px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !fileUrl) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      // 1. Check cache first
      const cached = await readCache(key);
      if (cancelled) return;

      if (cached) {
        setSrc(cached);
        setStatus("ready");
        return;
      }

      // 2. Generate thumbnail (fetch happens inside)
      setStatus("loading");

      try {
        const dataUrl = await enqueue(async () => {
          if (cancelled) throw new Error("cancelled");
          const url = await generateThumbnail(fileUrl, controller.signal);
          await wait(4);
          return url;
        });

        if (cancelled) return;

        await writeCache(key, dataUrl);
        setSrc(dataUrl);
        setStatus("ready");
      } catch (err) {
        if (!cancelled && err?.name !== "AbortError") {
          console.error("[Thumb] Failed:", err?.message, fileUrl);
          setStatus("error");
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isVisible, key, fileUrl]);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onViewClick) onViewClick();
    },
    [onViewClick]
  );

  const label =
    status === "error"
      ? "Tap to view 3D"
      : status === "ready"
      ? ""
      : "Loading preview...";

  return (
    <div
      ref={wrapRef}
      role="button"
      tabIndex={0}
      title="Click to view 3D model"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
      style={{
        width: "100%",
        height,
        backgroundColor: "#000",
        borderRadius: "4px",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <style>{`
        @keyframes stl-thumb-spin { to { transform: rotate(360deg); } }
      `}</style>

      {src ? (
        <img
          src={src}
          alt="3D preview"
          draggable={false}
          loading="lazy"
          decoding="async"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            padding: "6px",
            color: "#9fb3c8",
            fontSize: "10px",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {status === "loading" || status === "idle" ? (
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid rgba(255,255,255,0.25)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "stl-thumb-spin 0.8s linear infinite",
              }}
            />
          ) : (
            <span style={{ fontSize: "20px" }}>🦷</span>
          )}
          <span>{label}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(LazySTLThumbnail);